import { useEffect, useState } from 'react';

import { Button } from './shadcn/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './shadcn/select';

export interface PaginationProps {
  canGetNextPage: boolean;
  canGetPrevPage: boolean;
  prevPage: () => void;
  nextPage: () => void;
  currentPage: number;
  noOfPages: number | undefined;
  goToPage: (value: number) => void;
  testid?: string;
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  isNewSearch?: boolean;
  setIsNewSearch?: (value: boolean) => void;
  totalCount?: number;
  filteredCount?: number;
  entityName?: string;
}

export const Pagination = ({
  canGetPrevPage,
  canGetNextPage,
  prevPage,
  nextPage,
  currentPage,
  noOfPages,
  goToPage,
  pageSize = 50,
  onPageSizeChange,
  isNewSearch,
  setIsNewSearch,
  totalCount,
  filteredCount,
  entityName = 'items',
}: PaginationProps) => {
  const [displayPageCount, setDisplayPageCount] = useState(noOfPages);
  const [currentPageString, setCurrentPageString] = useState('1');

  const [displayTotalCount, setDisplayTotalCount] = useState(totalCount);
  const [displayFilteredCount, setDisplayFilteredCount] = useState(filteredCount);


  useEffect(() => {
    if (totalCount !== undefined) {
      setDisplayTotalCount(totalCount);
    }
  }, [totalCount]);

  useEffect(() => {
    if (filteredCount !== undefined) {
      setDisplayFilteredCount(filteredCount);
    }
  }, [filteredCount]);

  useEffect(() => {
    if (noOfPages !== undefined) {
      setDisplayPageCount(noOfPages);
    }
  }, [noOfPages]);

  useEffect(() => {
    if (isNewSearch) {
      goToPage && goToPage(0);
      setCurrentPageString('1');
      setIsNewSearch?.(false);
    }
  }, [isNewSearch]);

  useEffect(() => {
    if (currentPage !== parseInt(currentPageString) - 1) {
      setCurrentPageString((currentPage + 1).toString());
    }
  }, [currentPage]);

  const handleChangePage = (value: string) => {
    setCurrentPageString(value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (noOfPages === undefined) return;

    if (event.key === 'Enter') {
      const pageNumber = parseInt(currentPageString) - 1;

      if (pageNumber < 0 || pageNumber >= noOfPages) {
        goToPage && goToPage(currentPage);
        setCurrentPageString((currentPage + 1).toString());
        return;
      }

      goToPage && goToPage(pageNumber);
    }
  };

  return (
    <div className="flex h-auto items-center justify-between gap-4 rounded-md px-2 py-1.5 text-xs">
      <div className="justify- flex items-center">
        <Button
          className="p-1 disabled:cursor-not-allowed"
          variant="ghost"
          onClick={prevPage}
          disabled={!canGetPrevPage}
          data-testid="pagination-prev"
        >
          <span
            className={`iconify lucide--arrow-left-circle h-4 w-4 transition-colors duration-200 ${canGetPrevPage ? 'text-gray-800' : 'text-gray-400'}`}
          />
        </Button>

        <div className="flex flex-row items-center justify-center text-center">
          <input
            value={currentPageString}
            className="mx-1 h-5 w-8 rounded border px-1 disabled:bg-gray-100 disabled:text-gray-400"
            onChange={(e) => handleChangePage(e.target.value)}
            onKeyDown={handleKeyDown}
            inputMode="numeric"
            min="1"
            type="number"
            disabled={noOfPages === 1}
            data-testid="pagination-input"
          />
          <span className="pt-0.5">of {displayPageCount ?? 0}</span>
        </div>
        <Button
          className="p-1 disabled:cursor-not-allowed"
          variant="ghost"
          onClick={nextPage}
          disabled={!canGetNextPage}
          data-testid="pagination-next"
        >
          <span
            className={`iconify lucide--arrow-right-circle h-4 w-4 transition-colors duration-200 ${canGetNextPage ? 'text-gray-800' : 'text-gray-400'}`}
          />
        </Button>
      </div>

      {/* Count information */}
      {(displayTotalCount !== undefined || displayFilteredCount !== undefined) && (
        <div className="flex items-center gap-2 text-xs text-gray-600">
          {displayFilteredCount !== undefined &&
          displayTotalCount !== undefined &&
          displayFilteredCount !== displayTotalCount ? (
            <span>
              Showing {displayFilteredCount.toLocaleString()} of {displayTotalCount.toLocaleString()} {entityName}
            </span>
          ) : displayTotalCount !== undefined ? (
            <span>
              {displayTotalCount.toLocaleString()} {entityName}
            </span>
          ) : displayFilteredCount !== undefined ? (
            <span>
              {displayFilteredCount.toLocaleString()} {entityName}
            </span>
          ) : null}
        </div>
      )}

      {onPageSizeChange && (
        <div className="flex items-center gap-2">
          <span className="text-gray-600">Rows:</span>
          <Select value={pageSize.toString()} onValueChange={(value) => onPageSizeChange(parseInt(value))}>
            <SelectTrigger className="h-6 w-16 text-xs shadow-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="200">200</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};
